import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, MapPin, Mail, Phone, MessageCircle, Pencil, Edit2,
  Calendar, FileText, Users, Copy, ExternalLink, X, Lock,
  CheckCircle2, AlertTriangle, Plane, Wallet, Trash2, RefreshCw, Save, Send,
} from 'lucide-react';
import TravellerDocuments from '@/components/traveller/TravellerDocuments';
import TravellerComms from '@/components/traveller/TravellerComms';
import TravellerVisaDocs from '@/components/traveller/TravellerVisaDocs';
import TravellerIntelligencePanel from '@/components/traveller/TravellerIntelligencePanel';
import AiBriefPanel from '@/components/ai/AiBriefPanel';
import SensitivePii from '@/components/traveller/SensitivePii';
import WhatsAppSendDialog from '@/components/whatsapp/WhatsAppSendDialog';
import ExternalConversationLog from '@/components/whatsapp/ExternalConversationLog';
import ApplyCreditDialog from '@/components/credits/ApplyCreditDialog';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------
const formatCurrency = (amount) => {
  const n = Number(amount || 0);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const initialsOf = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join('')
    .toUpperCase();
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  return diff;
};

const TRIP_STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
  confirmed: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-rose-100 text-rose-700',
};

// BUG 5 — per-trip visa status chip (visa is per traveller per trip).
const VISA_CHIP_STYLES = {
  not_required: { label: 'Visa: Not required', cls: 'bg-slate-100 text-slate-600' },
  applied: { label: 'Visa: Applied', cls: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Visa: Approved', cls: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Visa: Rejected', cls: 'bg-rose-100 text-rose-700' },
  expired: { label: 'Visa: Expired', cls: 'bg-rose-100 text-rose-700' },
};

// ---------------------------------------------------------------------------
// Small subcomponents
// ---------------------------------------------------------------------------
function LtvStripItem({ label, value, tone = 'default', testId }) {
  const colorClass = tone === 'success' ? 'text-emerald-600'
    : tone === 'warn' ? 'text-amber-600'
    : 'text-slate-900';
  return (
    <div className="flex-1 px-4 py-3" data-testid={testId}>
      <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</div>
      <div className={`text-xl font-semibold mt-1 ${colorClass}`}>{value}</div>
    </div>
  );
}

function TagPill({ tag, onRemove, locked }) {
  return (
    <span
      data-testid={`tag-${tag}`}
      className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium border border-emerald-100"
    >
      {tag}
      {!locked && (
        <button
          type="button"
          aria-label={`Remove ${tag}`}
          onClick={onRemove}
          className="hover:text-rose-600"
          data-testid={`tag-remove-${tag}`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

function DocReadinessIndicator({ readiness, ready, total }) {
  const pct = total > 0 ? Math.round((ready / total) * 100) : 0;
  const bar = pct >= 75 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div className="flex items-center gap-3" data-testid="doc-readiness-indicator">
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${bar} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium text-slate-600 whitespace-nowrap">
        {ready}/{total} ready
      </span>
    </div>
  );
}

function QuickActionButton({ icon: Icon, label, color, onClick, testId }) {
  return (
    <Button
      variant="outline"
      onClick={onClick}
      data-testid={testId}
      className="gap-2 rounded-xl border-slate-200 hover:border-slate-300"
    >
      <Icon className="h-4 w-4" style={color ? { color } : undefined} />
      {label}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const TravellerDetail = () => {
  const { ucc } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const canDecideCredit = ['admin', 'accounts'].includes((user?.role || '').toLowerCase());
  const [loading, setLoading] = useState(true);
  const [traveller, setTraveller] = useState(null);
  const [trips, setTrips] = useState([]);
  const [stats, setStats] = useState(null);
  const [newTag, setNewTag] = useState('');
  const [tagInputOpen, setTagInputOpen] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [notesDirty, setNotesDirty] = useState(false);
  // Wallet credit balance (replaces the old cancellationCredits UI — the
  // legacy entries are still shown read-only further below, never edited).
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletCreditNotes, setWalletCreditNotes] = useState([]);
  const [applyDialogNote, setApplyDialogNote] = useState(null);
  const [legacyCredits, setLegacyCredits] = useState([]);
  // Admin LTV controls (Phase 16.1 — manual fix when computed value is wrong)
  const [ltvRecalcing, setLtvRecalcing] = useState(false);
  const [ltvOverrideOpen, setLtvOverrideOpen] = useState(false);
  const [ltvOverrideValue, setLtvOverrideValue] = useState('');
  const [waSendOpen, setWaSendOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, trRes, sRes, walletRes, legacyRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/clients/${ucc}`),
        axios.get(`${BACKEND_URL}/api/clients/${ucc}/trips`),
        axios.get(`${BACKEND_URL}/api/clients/${ucc}/stats`),
        axios.get(`${BACKEND_URL}/api/credit-notes/wallet/balance/${ucc}`).catch(() => ({ data: { availableBalance: 0, creditNotes: [] } })),
        axios.get(`${BACKEND_URL}/api/clients/${ucc}/credits`).catch(() => ({ data: { credits: [] } })),
      ]);
      setTraveller(tRes.data);
      setTrips(trRes.data || []);
      setStats(sRes.data);
      setNotesValue(tRes.data?.agentNotes || '');
      setNotesDirty(false);
      setWalletBalance(walletRes.data?.availableBalance || 0);
      setWalletCreditNotes(walletRes.data?.creditNotes || []);
      setLegacyCredits(legacyRes.data?.credits || []);
    } catch (error) {
      toast.error('Failed to load traveller');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  }, [ucc, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // PATCH helper — single field updates from inline edits.
  const patchTraveller = useCallback(async (body) => {
    try {
      const r = await axios.patch(`${BACKEND_URL}/api/clients/${ucc}`, body, { withCredentials: true });
      setTraveller(r.data);
      return true;
    } catch (err) {
      toast.error('Could not save change');
      return false;
    }
  }, [ucc]);

  const handleAddTag = async () => {
    const v = newTag.trim();
    if (!v) { setTagInputOpen(false); return; }
    const next = Array.from(new Set([...(traveller?.tags || []), v]));
    const ok = await patchTraveller({ tags: next });
    if (ok) {
      toast.success('Tag added');
      setNewTag('');
      setTagInputOpen(false);
    }
  };

  const handleRemoveTag = async (tag) => {
    const next = (traveller?.tags || []).filter((t) => t !== tag);
    const ok = await patchTraveller({ tags: next });
    if (ok) toast.success('Tag removed');
  };

  const handleSaveNotes = async () => {
    const ok = await patchTraveller({ agentNotes: notesValue });
    if (ok) {
      toast.success('Notes saved');
      setNotesDirty(false);
    }
  };

  // ---- Admin LTV controls --------------------------------------------------
  // Recalc walks the traveller's trips, recomputes LTV from `confirmed`
  // statuses, and writes the result back onto the client doc so the
  // travellers list view stays accurate. Manual override lets admin pin a
  // value that wins over the auto-computed one (used when historical data
  // is missing or wrong and a one-off correction is faster than fixing
  // every trip).
  const handleRecalcLtv = async () => {
    if (!isAdmin) return;
    setLtvRecalcing(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/clients/${ucc}/recalc-ltv`, {}, { withCredentials: true });
      const computed = Number(r.data?.ltvComputed || 0);
      const overridden = !!r.data?.ltvOverridden;
      toast.success(
        overridden
          ? `Recalculated: ₹${computed.toLocaleString('en-IN')} (override still active)`
          : `Recalculated: ₹${computed.toLocaleString('en-IN')}`,
      );
      // Refetch stats so the strip reflects the new effective LTV.
      const sRes = await axios.get(`${BACKEND_URL}/api/clients/${ucc}/stats`);
      setStats(sRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to recalculate LTV');
    } finally {
      setLtvRecalcing(false);
    }
  };

  const handleSaveLtvOverride = async () => {
    if (!isAdmin) return;
    const raw = ltvOverrideValue.trim();
    const value = raw === '' ? null : Number(raw);
    if (raw !== '' && (Number.isNaN(value) || value < 0)) {
      toast.error('Enter a positive number, or leave blank to clear');
      return;
    }
    try {
      await axios.patch(
        `${BACKEND_URL}/api/clients/${ucc}/ltv-override`,
        { value },
        { withCredentials: true },
      );
      toast.success(value === null ? 'Override cleared' : `Override set to ₹${value.toLocaleString('en-IN')}`);
      setLtvOverrideOpen(false);
      setLtvOverrideValue('');
      const sRes = await axios.get(`${BACKEND_URL}/api/clients/${ucc}/stats`);
      setStats(sRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save override');
    }
  };

  const handleClearLtvOverride = async () => {
    if (!isAdmin) return;
    if (!window.confirm('Clear the manual LTV override? The auto-computed value will be shown instead.')) return;
    try {
      await axios.patch(
        `${BACKEND_URL}/api/clients/${ucc}/ltv-override`,
        { value: null },
        { withCredentials: true },
      );
      toast.success('Override cleared');
      const sRes = await axios.get(`${BACKEND_URL}/api/clients/${ucc}/stats`);
      setStats(sRes.data);
    } catch (err) {
      toast.error('Failed to clear override');
    }
  };

  // ---- Wallet credit refresh (after Apply, via ApplyCreditDialog) --------
  const refetchWalletBalance = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/credit-notes/wallet/balance/${ucc}`);
      setWalletBalance(r.data?.availableBalance || 0);
      setWalletCreditNotes(r.data?.creditNotes || []);
    } catch (err) {
      // non-critical
    }
  };

  const generatePortalLink = async (tripId) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/trips/${tripId}/generate-portal-link`);
      const link = response.data.portal_url || `${window.location.origin}/portal/${response.data.token}`;
      await navigator.clipboard.writeText(link);
      toast.success('Portal link copied to clipboard!');
    } catch (error) {
      toast.error('Failed to generate portal link');
    }
  };

  const openWhatsApp = () => {
    if (!traveller?.phone) { toast.error('No phone on file'); return; }
    const num = `${(traveller.countryCode || '').replace(/[^0-9+]/g, '')}${traveller.phone}`.replace(/^\+/, '');
    window.open(`https://wa.me/${num}`, '_blank', 'noopener');
  };

  const openCall = () => {
    if (!traveller?.phone) { toast.error('No phone on file'); return; }
    window.location.href = `tel:${(traveller.countryCode || '')}${traveller.phone}`;
  };

  const openEmail = () => {
    if (!traveller?.email) { toast.error('No email on file'); return; }
    window.location.href = `mailto:${traveller.email}`;
  };

  const tags = traveller?.tags || [];
  const upcomingTrip = stats?.upcomingTrip;

  // Passport expiry warning — flag when expiring within 90 days (or already
  // expired). Source field set by OCR / manual edit.
  const passportExpiryDays = useMemo(() => {
    const exp = traveller?.passportExpiryDate;
    if (!exp) return null;
    const d = new Date(exp);
    if (isNaN(d.getTime())) return null;
    return Math.floor((d.getTime() - Date.now()) / 86400000);
  }, [traveller?.passportExpiryDate]);
  const upcomingDays = upcomingTrip ? daysUntil(upcomingTrip.departureDate || upcomingTrip.travelDateFrom || upcomingTrip.startDate) : null;

  // Top destinations from confirmed trip history
  const destinationSummary = useMemo(() => {
    const counts = {};
    trips.forEach((t) => {
      const dest = t.destination || 'Unknown';
      if (!counts[dest]) counts[dest] = { destination: dest, count: 0 };
      counts[dest].count += 1;
    });
    return Object.values(counts).sort((a, b) => b.count - a.count).slice(0, 5);
  }, [trips]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="traveller-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-emerald-600"></div>
      </div>
    );
  }
  if (!traveller) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6" data-testid="traveller-detail-page">
      {/* Back */}
      <Button
        variant="ghost"
        onClick={() => navigate('/clients')}
        data-testid="back-btn"
        className="text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Travellers
      </Button>

      {/* HERO — Avatar + name + tags + quick actions */}
      <Card className="p-6 bg-white border border-slate-200 rounded-2xl shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div
            data-testid="traveller-avatar"
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white flex items-center justify-center text-2xl font-bold flex-shrink-0"
          >
            {initialsOf(traveller.fullName)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1
                data-testid="traveller-name"
                className="text-2xl sm:text-3xl font-semibold text-slate-900 truncate"
              >
                {traveller.fullName || '(Pending traveller)'}
              </h1>
              <button
                type="button"
                onClick={() => navigate(`/clients/${ucc}/edit`)}
                aria-label="Edit traveller"
                className="text-slate-400 hover:text-slate-700"
                data-testid="edit-traveller-pencil"
              >
                <Pencil className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-1 text-sm text-slate-500 font-mono truncate">
              <span data-testid="traveller-display-ucc">{traveller.displayUCC || traveller.ucc}</span>
              {traveller.travelCategory && <span className="ml-2 capitalize">· {traveller.travelCategory}</span>}
              {traveller.email && <span className="ml-2 truncate">· {traveller.email}</span>}
            </div>
            {traveller.ownerAgentName && (
              <div className="mt-1 text-xs text-slate-500" data-testid="traveller-owner-agent">
                Owning agent: <span className="font-medium text-slate-700">{traveller.ownerAgentName}</span>
              </div>
            )}
            {traveller.phone && (
              <a
                href={`tel:${traveller.countryCode || ''}${traveller.phone}`}
                data-testid="traveller-phone-link"
                className="mt-1 inline-flex items-center gap-1.5 text-sm text-teal-700 hover:text-teal-900"
              >
                <Phone className="h-3.5 w-3.5" /> {traveller.countryCode || ''} {traveller.phone}
              </a>
            )}
            {passportExpiryDays !== null && passportExpiryDays < 90 && (
              <div className="mt-2" data-testid="passport-expiry-badge">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                  passportExpiryDays < 0
                    ? 'bg-red-50 text-red-700 border-red-200'
                    : 'bg-amber-50 text-amber-700 border-amber-200'
                }`}>
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {passportExpiryDays < 0
                    ? 'Passport expired'
                    : `Passport expires in ${passportExpiryDays} day${passportExpiryDays === 1 ? '' : 's'}`}
                </span>
              </div>
            )}
            {/* Tags row */}
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="traveller-tags">
              {tags.map((tag) => (
                <TagPill key={tag} tag={tag} onRemove={() => handleRemoveTag(tag)} />
              ))}
              {tagInputOpen ? (
                <div className="inline-flex items-center gap-1">
                  <input
                    type="text"
                    autoFocus
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddTag();
                      if (e.key === 'Escape') { setNewTag(''); setTagInputOpen(false); }
                    }}
                    onBlur={handleAddTag}
                    placeholder="tag…"
                    data-testid="tag-input"
                    className="px-2 py-0.5 text-xs border border-emerald-300 rounded-full focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setTagInputOpen(true)}
                  data-testid="tag-add-btn"
                  className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border border-dashed border-slate-300 text-xs text-slate-500 hover:text-emerald-700 hover:border-emerald-400"
                >
                  <Plus className="h-3 w-3" /> Add tag
                </button>
              )}
            </div>
          </div>
          {/* Quick actions */}
          <div className="flex flex-wrap gap-2 sm:flex-nowrap" data-testid="quick-actions">
            <QuickActionButton icon={Phone} label="Call" color="#0d9488" onClick={openCall} testId="quick-call-btn" />
            <QuickActionButton icon={MessageCircle} label="WhatsApp" color="#25D366" onClick={openWhatsApp} testId="quick-whatsapp-btn" />
            <QuickActionButton icon={MessageCircle} label="WA Business" color="#0BA888" onClick={() => setWaSendOpen(true)} testId="quick-whatsapp-cloud-btn" />
            <QuickActionButton icon={Mail} label="Email" onClick={openEmail} testId="quick-email-btn" />
            <Button
              onClick={() => navigate(`/quotes/new?client=${ucc}`)}
              data-testid="create-trip-btn"
              className="gap-2 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white"
            >
              <Plus className="h-4 w-4" /> New Trip
            </Button>
          </div>
        </div>
      </Card>

      {/* LTV STRIP */}
      <Card className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="ltv-strip">
        <div className="flex flex-wrap divide-x divide-slate-200">
          <LtvStripItem
            label="Lifetime value"
            value={stats ? formatCurrency(stats.ltv) : '—'}
            tone="success"
            testId="stat-ltv"
          />
          <LtvStripItem
            label="Total trips"
            value={stats ? String(stats.totalTrips) : '—'}
            testId="stat-total-trips"
          />
          <LtvStripItem
            label="Last trip"
            value={stats?.lastTripDate ? formatDate(stats.lastTripDate) : 'No trips yet'}
            testId="stat-last-trip"
          />
          <LtvStripItem
            label="Avg trip value"
            value={stats && stats.confirmedTrips > 0 ? formatCurrency(stats.avgTripValue) : '—'}
            tone="success"
            testId="stat-avg-value"
          />
        </div>
        {/* Phase 17 — ledger drill-down link. Footer row keeps the strip
            visually unified while pointing power-users to the cashbook. */}
        <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/60 text-right">
          <Link
            to={`/ledger?party_type=traveller&party_id=${ucc}`}
            data-testid="view-ledger-link"
            className="text-[11px] font-semibold text-teal-700 hover:text-teal-800 hover:underline"
          >
            View ledger →
          </Link>
        </div>
        {isAdmin && (
          <div
            data-testid="ltv-admin-bar"
            className="flex flex-wrap items-center gap-2 px-4 py-2 bg-slate-50 border-t border-slate-200 text-xs"
          >
            <span className="text-slate-500 uppercase tracking-wider font-semibold mr-1">Admin</span>
            {stats?.ltvOverridden ? (
              <span
                data-testid="ltv-override-badge"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 font-medium"
              >
                Override active · auto = {formatCurrency(stats.ltvComputed)}
              </span>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              onClick={handleRecalcLtv}
              disabled={ltvRecalcing}
              data-testid="ltv-recalc-btn"
              className="h-7 text-xs gap-1.5"
            >
              <RefreshCw className={`h-3 w-3 ${ltvRecalcing ? 'animate-spin' : ''}`} />
              {ltvRecalcing ? 'Recalculating…' : 'Recalc LTV'}
            </Button>
            {!ltvOverrideOpen ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setLtvOverrideOpen(true);
                  setLtvOverrideValue(
                    stats?.ltvOverride !== null && stats?.ltvOverride !== undefined
                      ? String(stats.ltvOverride)
                      : '',
                  );
                }}
                data-testid="ltv-override-open-btn"
                className="h-7 text-xs gap-1.5"
              >
                <Edit2 className="h-3 w-3" /> {stats?.ltvOverridden ? 'Edit override' : 'Override LTV'}
              </Button>
            ) : (
              <div className="inline-flex items-center gap-1.5">
                <Input
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  placeholder="₹ amount"
                  value={ltvOverrideValue}
                  onChange={(e) => setLtvOverrideValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveLtvOverride();
                    if (e.key === 'Escape') { setLtvOverrideOpen(false); setLtvOverrideValue(''); }
                  }}
                  data-testid="ltv-override-input"
                  className="h-7 w-32 text-xs"
                  autoFocus
                />
                <Button
                  size="sm"
                  onClick={handleSaveLtvOverride}
                  data-testid="ltv-override-save-btn"
                  className="h-7 text-xs gap-1 bg-emerald-700 hover:bg-emerald-800 text-white"
                >
                  <Save className="h-3 w-3" /> Save
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setLtvOverrideOpen(false); setLtvOverrideValue(''); }}
                  data-testid="ltv-override-cancel-btn"
                  className="h-7 text-xs"
                >
                  Cancel
                </Button>
              </div>
            )}
            {stats?.ltvOverridden && !ltvOverrideOpen && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleClearLtvOverride}
                data-testid="ltv-override-clear-btn"
                className="h-7 text-xs text-rose-700 hover:text-rose-800"
              >
                Clear override
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* BATCH 10 — Traveller LTV + Quote Intelligence (deterministic, no AI) */}
      <TravellerIntelligencePanel ucc={ucc} />

      {/* BATCH 10 · Session 3 — AI Brief (uses Session 1-2's numbers) */}
      <AiBriefPanel contextType="traveller" contextId={ucc} />

      {/* UPCOMING TRIP */}
      {upcomingTrip && (
        <Card className="p-4 bg-amber-50 border border-amber-200 rounded-2xl" data-testid="upcoming-trip-card">
          <div className="flex items-center gap-4 flex-wrap sm:flex-nowrap">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Plane className="h-5 w-5 text-amber-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-amber-900">
                {upcomingTrip.destination || 'Trip'} · <span className="font-mono">{upcomingTrip.displayTripId || upcomingTrip.tripId || upcomingTrip.quoteNumber || ''}</span>
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                {formatDate(upcomingTrip.departureDate || upcomingTrip.travelDateFrom || upcomingTrip.startDate)}
                {upcomingTrip.numTravellers ? ` · ${upcomingTrip.numTravellers} pax` : ''}
              </div>
            </div>
            {upcomingDays !== null && (
              <div className="text-right">
                <div className="text-2xl font-bold text-amber-900">{upcomingDays}d</div>
                <div className="text-[10px] uppercase tracking-wider text-amber-700">to departure</div>
              </div>
            )}
            <Button
              size="sm"
              onClick={() => navigate(`/quotes/${upcomingTrip.id || upcomingTrip._id}/edit`)}
              data-testid="upcoming-open-btn"
              className="bg-amber-700 hover:bg-amber-800 text-white"
            >
              Open <ExternalLink className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        </Card>
      )}

      {/* Phase 18 #1 — Resend / WhatsApp the traveller's latest voucher & invoice */}
      <Card className="p-5 bg-white border border-slate-200 rounded-2xl shadow-sm" data-testid="send-documents-card">
        <div className="flex items-center gap-2 mb-3">
          <Send className="h-4 w-4 text-slate-500" />
          <h2 className="text-base font-semibold text-slate-900">Send Documents</h2>
        </div>
        <TravellerComms ucc={ucc} traveller={traveller} />
      </Card>

      <div className="mt-2">
        <ExternalConversationLog contextType="traveller" contextId={ucc} />
      </div>

      {waSendOpen && (
        <WhatsAppSendDialog
          open={waSendOpen}
          onOpenChange={setWaSendOpen}
          contextType="traveller"
          contextId={ucc}
          title={traveller.fullName}
        />
      )}

      <ApplyCreditDialog
        creditNote={applyDialogNote}
        open={!!applyDialogNote}
        onOpenChange={(v) => { if (!v) setApplyDialogNote(null); }}
        onDone={refetchWalletBalance}
      />

      {/* BATCH 1 — Sensitive identity fields (PAN / Passport / Aadhaar) */}
      <SensitivePii
        ucc={ucc}
        traveller={traveller}
        trips={trips}
        role={(user?.role || '').toLowerCase()}
      />

      {/* Two-col grid: Documents | Trip History */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Documents readiness summary */}
        <Card className="p-5 bg-white border border-slate-200 rounded-2xl shadow-sm" data-testid="documents-summary-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <FileText className="h-4 w-4 text-slate-500" />
              Document Readiness
            </h2>
          </div>
          {stats && (
            <DocReadinessIndicator
              readiness={stats.docReadiness}
              ready={stats.docReadyCount}
              total={stats.docTotal}
            />
          )}
          <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
            {stats && Object.entries(stats.docReadiness).map(([key, ok]) => (
              <div
                key={key}
                data-testid={`doc-readiness-${key}`}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50"
              >
                {ok
                  ? <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                  : <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />}
                <span className="capitalize text-slate-700">{key.replace('_', ' ')}</span>
                <span className={`ml-auto text-xs font-medium ${ok ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {ok ? 'On file' : 'Missing'}
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* Trip History */}
        <Card className="p-5 bg-white border border-slate-200 rounded-2xl shadow-sm" data-testid="trip-history-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-slate-500" />
              Trip History
            </h2>
            <span className="text-xs text-slate-500">{trips.length} total</span>
          </div>
          {trips.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-xl">
              <Plane className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No trips yet</p>
              <Button
                size="sm"
                variant="link"
                onClick={() => navigate(`/quotes/new?client=${ucc}`)}
                className="text-emerald-700"
                data-testid="empty-create-trip-btn"
              >
                Create the first trip →
              </Button>
            </div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {trips.map((trip) => {
                const status = (trip.status || 'draft').toLowerCase();
                const badgeCls = TRIP_STATUS_STYLES[status] || TRIP_STATUS_STYLES.draft;
                const visa = VISA_CHIP_STYLES[trip.viewerVisaStatus];
                return (
                  <div
                    key={trip.id || trip._id}
                    data-testid={`trip-row-${trip.tripId || trip.id}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <MapPin className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {trip.destination || '—'}
                      </div>
                      <div className="text-xs text-slate-500 font-mono truncate">
                        {trip.displayTripId || trip.tripId || trip.quoteNumber} · {formatDate(trip.createdAt)}
                      </div>
                    </div>
                    {visa && (
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${visa.cls}`} data-testid={`trip-visa-${trip.tripId || trip.id}`}>
                        {visa.label}
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase ${badgeCls}`}>
                      {status}
                    </span>
                    <span className="text-sm font-semibold text-slate-700 whitespace-nowrap">
                      {formatCurrency(trip.totalCost)}
                    </span>
                    <div className="flex">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => navigate(`/quotes/${trip.id || trip._id}/edit`)}
                        data-testid={`trip-open-${trip.tripId || trip.id}`}
                        className="h-8 w-8 text-slate-500 hover:text-slate-900"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => generatePortalLink(trip.id || trip._id)}
                        data-testid={`trip-portal-${trip.tripId || trip.id}`}
                        className="h-8 w-8 text-slate-500 hover:text-emerald-700"
                        title="Copy portal link"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {destinationSummary.length > 1 && (
            <div className="mt-4 pt-3 border-t border-slate-100">
              <div className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-2">
                Destinations covered
              </div>
              <div className="flex flex-wrap gap-2" data-testid="destinations-covered">
                {destinationSummary.map((d) => (
                  <span
                    key={d.destination}
                    className="px-2.5 py-0.5 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-100"
                  >
                    {d.destination} <span className="text-emerald-500">· {d.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* WALLET CREDIT — replaces the old editable "Cancellation Credit"
          card. Agents see balance read-only; only Admin/Accounts can Apply
          (create happens via the Cancel Trip flow, no manual create here). */}
      <Card className="p-5 bg-white border border-slate-200 rounded-2xl shadow-sm" data-testid="wallet-credit-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Wallet className="h-4 w-4 text-slate-500" />
            Wallet Credit
            <span
              data-testid="wallet-credit-balance-pill"
              className={`ml-2 px-2 py-0.5 rounded-full text-xs font-mono ${walletBalance > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
            >
              Credit available: ₹{Math.round(walletBalance).toLocaleString('en-IN')}
            </span>
          </h2>
        </div>
        {walletCreditNotes.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No wallet credit history yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {walletCreditNotes.map((cn) => (
              <li key={cn.creditNoteId} data-testid={`wallet-credit-row-${cn.creditNoteId}`} className="py-2 flex items-center gap-3 text-sm">
                <span className={`text-sm font-mono font-semibold ${cn.remainingAmount > 0 ? 'text-emerald-700' : 'text-slate-400 line-through'}`}>
                  ₹{Math.round(cn.remainingAmount).toLocaleString('en-IN')}
                </span>
                <span className="flex-1 text-xs text-slate-500 truncate">
                  {cn.creditNoteNumber} · {cn.reason}
                  {cn.sourceDisplayTripId && <span className="ml-2 font-mono">from {cn.sourceDisplayTripId}</span>}
                  <span className="ml-2 text-slate-400">of ₹{Math.round(cn.originalAmount).toLocaleString('en-IN')} original</span>
                </span>
                <span className="text-[10px] text-slate-400">{(cn.createdAt || '').slice(0, 10)}</span>
                {canDecideCredit && cn.remainingAmount > 0 && (
                  <Button
                    size="sm" variant="outline"
                    onClick={() => setApplyDialogNote(cn)}
                    data-testid={`apply-wallet-credit-btn-${cn.creditNoteId}`}
                    className="text-xs h-7"
                  >
                    Apply
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* OLDER CREDITS (legacy, pre-wallet-credit system) — read-only, never
          edited or deleted; will be migrated in a future session. */}
      {legacyCredits.length > 0 && (
        <Card className="p-5 bg-white border border-slate-200 rounded-2xl shadow-sm" data-testid="legacy-credits-card">
          <h2 className="text-base font-semibold text-slate-900 mb-3">Older credits (legacy)</h2>
          <ul className="divide-y divide-slate-100">
            {legacyCredits.map((c) => (
              <li key={c.creditId} data-testid={`legacy-credit-row-${c.creditId}`} className="py-2 flex items-center gap-3 text-sm">
                <span className={`text-sm font-mono font-semibold ${c.appliedTripId ? 'text-slate-400 line-through' : 'text-emerald-700'}`}>
                  ₹{Math.round(c.amount).toLocaleString('en-IN')}
                </span>
                <span className="flex-1 text-xs text-slate-500 truncate">
                  {c.sourceTripId ? <span className="font-mono">{c.sourceTripId}</span> : 'No source trip'}
                  {c.note && <span className="ml-2 italic">"{c.note}"</span>}
                  {c.appliedTripId && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">
                      Applied → {c.appliedTripId}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}


      {/* AGENT NOTES */}
      <Card className="p-5 bg-white border border-slate-200 rounded-2xl shadow-sm" data-testid="agent-notes-card">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Edit2 className="h-4 w-4 text-slate-500" />
            Agent Notes
            <Lock className="h-3 w-3 text-slate-400" title="Private — only visible to your team" />
          </h2>
          {notesDirty && (
            <Button
              size="sm"
              onClick={handleSaveNotes}
              data-testid="save-notes-btn"
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
            >
              Save
            </Button>
          )}
        </div>
        <Textarea
          value={notesValue}
          onChange={(e) => { setNotesValue(e.target.value); setNotesDirty(true); }}
          onBlur={() => { if (notesDirty) handleSaveNotes(); }}
          rows={3}
          data-testid="agent-notes-textarea"
          placeholder="Preferences, special requests, follow-up context, dietary needs…"
          className="border-slate-200 rounded-xl resize-none focus:border-emerald-500 focus:ring-emerald-500"
        />
      </Card>

      {/* DOCUMENTS UPLOAD (existing component preserved) */}
      <TravellerDocuments
        ucc={ucc}
        familyMembers={traveller.familyMembers || []}
        onFamilyChange={fetchAll}
        onAutofill={async (extracted, docType) => {
          const updates = {};
          if (extracted.passportNumber) updates.passportNumber = extracted.passportNumber;
          if (extracted.passportIssueDate) updates.passportIssueDate = extracted.passportIssueDate;
          if (extracted.passportExpiryDate) updates.passportExpiryDate = extracted.passportExpiryDate;
          if (extracted.passportIssuedAt) updates.passportIssuedAt = extracted.passportIssuedAt;
          if (extracted.panCard) updates.panCard = extracted.panCard;
          if (extracted.aadharCard) updates.aadharCard = extracted.aadharCard;
          if (extracted.voterIdNumber) updates.voterIdNumber = extracted.voterIdNumber;
          if (extracted.licenseNumber) updates.licenseNumber = extracted.licenseNumber;
          if (extracted.address) updates.address = extracted.address;
          if (extracted.pinCode) updates.pincode = extracted.pinCode;
          if (Object.keys(updates).length === 0) return;
          try {
            await axios.patch(`${BACKEND_URL}/api/clients/${ucc}`, updates, { withCredentials: true });
            toast.success(`${Object.keys(updates).length} field(s) auto-filled from ${docType}`);
            fetchAll();
          } catch (err) {
            toast.error('Could not auto-fill traveller fields');
          }
        }}
      />

      {/* VISA DOCS — preserved as-is. Existing component shows Trip-linked
          visa uploads when scoped per trip from inside the Trip Financials
          page. */}
      <TravellerVisaDocs ucc={ucc} trips={trips} />

      <FrequentFlyersSection ucc={ucc} initial={traveller.frequentFlyers || []} />

      <DangerZone ucc={ucc} fullName={traveller.fullName} />
    </div>
  );
};

// ============================================================================
// Frequent Flyer Programs (Phase 15.5)
//
// Each row = { airline, code, tier? }. Persists into clients.frequentFlyers
// via PATCH /api/clients/:ucc. Designed as inline editor (no modal) so the
// agent can paste FFP codes during a single profile-edit session.
// ============================================================================
const FrequentFlyersSection = ({ ucc, initial }) => {
  const [rows, setRows] = useState(initial || []);
  const [airline, setAirline] = useState('');
  const [code, setCode] = useState('');
  const [tier, setTier] = useState('');
  const [saving, setSaving] = useState(false);

  const persist = async (nextRows) => {
    setSaving(true);
    try {
      await axios.patch(`${BACKEND_URL}/api/clients/${ucc}`, { frequentFlyers: nextRows }, { withCredentials: true });
      setRows(nextRows);
      toast.success('Frequent flyer programs updated');
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const add = async () => {
    const a = (airline || '').trim();
    const c = (code || '').trim();
    if (!a || !c) { toast.error('Airline and FF code are required'); return; }
    const next = [...rows, { airline: a, code: c, tier: (tier || '').trim() }];
    await persist(next);
    setAirline(''); setCode(''); setTier('');
  };

  const removeAt = async (idx) => {
    const next = rows.filter((_, i) => i !== idx);
    await persist(next);
  };

  return (
    <Card data-testid="frequent-flyers-section">
      <CardHeader>
        <CardTitle className="text-base">Frequent Flyer Programs</CardTitle>
        <CardDescription className="text-xs">
          Airline-specific FF codes so they're pre-filled on new bookings.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="text-xs text-slate-400 mb-4">No FFP entries yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100 mb-4">
            {rows.map((r, idx) => (
              <FFPRow key={`${r.airline}-${r.code}-${idx}`} row={r} idx={idx} onRemove={removeAt} disabled={saving} />
            ))}
          </ul>
        )}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <Input placeholder="Airline (e.g. Air India)" value={airline} onChange={(e) => setAirline(e.target.value)} data-testid="ffp-airline-input" />
          <Input placeholder="FF Code" value={code} onChange={(e) => setCode(e.target.value)} data-testid="ffp-code-input" />
          <Input placeholder="Tier (optional)" value={tier} onChange={(e) => setTier(e.target.value)} data-testid="ffp-tier-input" />
          <Button onClick={add} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="ffp-add-btn">
            {saving ? 'Saving…' : 'Add'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const FFPRow = ({ row, idx, onRemove, disabled }) => (
  <li className="flex items-center justify-between gap-3 py-2.5" data-testid={`ffp-row-${idx}`}>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold text-slate-900 truncate">{row.airline}</div>
      <div className="text-xs text-slate-500 font-mono">{row.code}{row.tier ? ` · ${row.tier}` : ''}</div>
    </div>
    <Button variant="ghost" size="sm" onClick={() => onRemove(idx)} disabled={disabled} className="text-rose-600 hover:bg-rose-50" data-testid={`ffp-remove-${idx}`}>
      Remove
    </Button>
  </li>
);

// ============================================================================
// Danger Zone — soft-delete the traveller (Phase 15).
//
// Calls DELETE /api/clients/:ucc. Backend returns 409 with the exact message
// "Cannot delete — traveller has active trips." when the operator must close
// or transfer trips before the delete is allowed; we surface that message
// verbatim in the toast so the user knows what to do next.
// ============================================================================
const DangerZone = ({ ucc, fullName }) => {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const onDelete = async () => {
    if (confirm !== fullName) {
      toast.error('Type the traveller\'s full name to confirm');
      return;
    }
    setBusy(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/clients/${ucc}`, { withCredentials: true });
      toast.success('Traveller deleted');
      navigate('/clients');
    } catch (err) {
      const detail = (err && err.response && err.response.data && err.response.data.detail) || 'Delete failed';
      toast.error(typeof detail === 'string' ? detail : 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-rose-200 bg-rose-50/30" data-testid="danger-zone">
      <CardHeader>
        <CardTitle className="text-rose-700 text-base">Danger zone</CardTitle>
        <CardDescription className="text-xs">
          Deleting hides the traveller everywhere in the app. Trips remain
          intact for history. You'll need to close or transfer active trips first.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!open ? (
          <Button
            variant="outline"
            onClick={() => setOpen(true)}
            data-testid="delete-traveller-btn"
            className="border-rose-200 text-rose-700 hover:bg-rose-100"
          >
            <Trash2 className="h-4 w-4 mr-2" /> Delete traveller
          </Button>
        ) : (
          <div className="space-y-3">
            <div className="text-xs text-slate-700">
              Type <span className="font-mono font-semibold">{fullName}</span> to confirm:
            </div>
            <Input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Full name…"
              data-testid="delete-confirm-input"
              className="max-w-sm"
            />
            <div className="flex gap-2">
              <Button
                onClick={onDelete}
                disabled={busy}
                data-testid="confirm-delete-btn"
                className="bg-rose-600 hover:bg-rose-700 text-white"
              >
                {busy ? 'Deleting…' : 'Confirm delete'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => { setOpen(false); setConfirm(''); }}
                disabled={busy}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TravellerDetail;
